import React, { useState, useCallback } from 'react';
import {
  Calculator, ChevronDown, ChevronRight,
  Package, Layers, Tag, TrendingUp,
  AlertTriangle, CheckSquare, Square, Save, Check, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';
import { useAuth } from '../context/AuthContext';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';
import { SidePanel } from '../components/SidePanel';

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmtEur = (v) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2
  }).format(v);
};

const fmtQty = (v) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 }).format(v);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function StocksValorisation() {
  const { activeAgences } = useSociete();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Expanded sets
  const [expandedFamilles, setExpandedFamilles] = useState(new Set());
  const [expandedSFs, setExpandedSFs] = useState(new Set());

  // Filtres
  const [filtreFamille,    setFiltreFamille]    = useState('');
  const [filtreSousFam,    setFiltreSousFam]    = useState('');
  const [filtreMarque,     setFiltreMarque]     = useState('');

  // PMP init panel
  const [showPmpPanel, setShowPmpPanel]     = useState(false);
  const [pmpItems, setPmpItems]             = useState([]);
  const [savingPmp, setSavingPmp]           = useState(false);
  const [savingIdx, setSavingIdx]           = useState(null);
  const [bulkDate, setBulkDate]             = useState(today);

  const toggleFamille = (key) =>
    setExpandedFamilles(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });

  const toggleSF = (key) =>
    setExpandedSFs(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });

  const calculate = useCallback(async () => {
    if (!date) { toast.error('Sélectionnez une date'); return; }
    setLoading(true);
    try {
      const res = await api.getStocksValorisation({ date_valorisation: date });
      setData(res.data);
      // Auto-expand all familles, collapse sous-familles
      setExpandedFamilles(new Set(res.data.familles.map(f => f.famille_id || '__sans_famille__')));
      setExpandedSFs(new Set());
    } catch {
      toast.error('Erreur lors du calcul de la valorisation');
    } finally {
      setLoading(false);
    }
  }, [date]);

  const [pdfLoading, setPdfLoading] = useState(false);
  const printPdf = useCallback(async () => {
    if (!date || !data) return;
    setPdfLoading(true);
    try {
      const res = await api.getStocksValorisationPdf({ date_valorisation: date, mode_qty: modeQty, mode_prix: modePrix });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (!w) toast.error('Autorisez les pop-ups pour ouvrir le PDF');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [date, data]);

  const agenceNom = activeAgences.length === 1
    ? activeAgences[0].nom
    : activeAgences.length > 1
      ? activeAgences.map(a => a.nom).join(', ')
      : 'Toutes agences';

  // ── PMP panel helpers ────────────────────────────────────────────────────
  const openPmpPanel = useCallback(async () => {
    if (!data?.articles_sans_prix?.length) return;
    setPmpItems(data.articles_sans_prix.map(a => ({
      ...a,
      pmp:         a.prix_achat != null ? String(a.prix_achat) : '',
      date_entree: today,
      selected:    true,
    })));
    setShowPmpPanel(true);
  }, [data, today]);

  const toggleAll = () => {
    const allSelected = pmpItems.every(i => i.selected);
    setPmpItems(prev => prev.map(i => ({ ...i, selected: !allSelected })));
  };

  const applyBulkDate = () => {
    if (!bulkDate) return;
    setPmpItems(prev => prev.map(i => i.selected ? { ...i, date_entree: bulkDate } : i));
  };

  const updateItem = (idx, field, value) => {
    setPmpItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const savePmp = async () => {
    const toSave = pmpItems.filter(i => i.selected && i.pmp !== '' && parseFloat(i.pmp) > 0);
    if (!toSave.length) { toast.error('Aucun article sélectionné avec un PMP valide'); return; }
    setSavingPmp(true);
    try {
      const payload = toSave.map(i => ({
        article_id:  i.article_id,
        stock_id:    i.stock_id,
        pmp:         parseFloat(i.pmp),
        date_entree: i.date_entree,
        quantite:    i.quantite,
      }));
      const res = await api.postPmpInit(payload);
      toast.success(`${res.data.updated} article(s) initialisé(s)`);
      setShowPmpPanel(false);
      if (data) calculate();
    } catch {
      toast.error('Erreur lors de l\'initialisation des PMP');
    } finally {
      setSavingPmp(false);
    }
  };

  const saveOne = async (idx) => {
    const item = pmpItems[idx];
    if (!item.pmp || parseFloat(item.pmp) <= 0) {
      toast.error('PMP invalide');
      return;
    }
    setSavingIdx(idx);
    try {
      const res = await api.postPmpInit([{
        article_id:  item.article_id,
        stock_id:    item.stock_id,
        pmp:         parseFloat(item.pmp),
        date_entree: item.date_entree,
        quantite:    item.quantite,
      }]);
      if (res.data.updated > 0) {
        toast.success(`PMP initialisé — ${item.article_ref || item.article_nom}`);
        // Retirer l'item de la liste
        setPmpItems(prev => prev.filter((_, i) => i !== idx));
        if (data) calculate();
      } else {
        toast.error('Article introuvable en base');
      }
    } catch {
      toast.error('Erreur lors de l\'initialisation');
    } finally {
      setSavingIdx(null);
    }
  };

  // Compte articles sans prix dans les résultats courants
  const nbSansPmp = data?.articles_sans_prix?.length ?? 0;

  // ── Toggles Mode ──────────────────────────────────────────────────────────
  const [modeQty,  setModeQty]  = useState('physique');   // 'physique' | 'comptable'
  const [modePrix, setModePrix] = useState('pmp');        // 'pmp' | 'achat'

  // Clé de valeur active selon les toggles
  const valKey = `val_${modeQty}_${modePrix === 'pmp' ? 'pmp' : 'achat'}`;
  const getValeur  = (art) => art[valKey] ?? 0;
  const getQty     = (art) => modeQty === 'physique' ? (art.quantite_physique ?? art.quantite ?? 0) : (art.quantite_comptable ?? art.quantite ?? 0);
  const getPrix    = (art) => modePrix === 'pmp' ? (art.pmp ?? art.prix_achat ?? 0) : (art.prix_achat ?? art.pmp ?? 0);
  const labelQty   = modeQty  === 'physique' ? 'Q. Physique' : 'Q. Comptable';
  const labelPrix  = modePrix === 'pmp'      ? 'PMP / C.Revient' : 'Prix achat HT';

  const BASE_PRIX_BADGE = {
    dossier_revient: { label: 'Dossier revient', bg: '#DCFCE7', color: '#15803D' },
    pmp:             { label: 'PMP calculé',      bg: '#EFF6FF', color: '#1D4ED8' },
    prix_achat:      { label: 'Prix achat',        bg: '#F0F9FF', color: '#0369A1' },
    non_renseigne:   { label: 'Non renseigné',     bg: '#FEF2F2', color: '#DC2626' },
  };

  // Options de filtres (extraites des résultats)
  const allFamilles   = data ? data.familles.map(f => ({ id: f.famille_id || '', nom: f.famille_nom })) : [];
  const allSousFams   = data && filtreFamille
    ? (data.familles.find(f => (f.famille_id || '') === filtreFamille)?.sous_familles || [])
        .map(sf => ({ id: sf.sous_famille_id || '', nom: sf.sous_famille_nom }))
    : data
      ? data.familles.flatMap(f => f.sous_familles).map(sf => ({ id: sf.sous_famille_id || '', nom: sf.sous_famille_nom }))
      : [];
  const allMarques = data
    ? [...new Set(
        data.familles
          .flatMap(f => f.sous_familles)
          .flatMap(sf => sf.articles)
          .map(a => a.marque_nom)
          .filter(Boolean)
      )].sort()
    : [];

  // Filtrage client-side + totaux dynamiques
  const famillesFiltrees = data
    ? data.familles
        .filter(f => !filtreFamille    || (f.famille_id || '') === filtreFamille)
        .map(f => ({
          ...f,
          sous_familles: f.sous_familles
            .filter(sf => !filtreSousFam || (sf.sous_famille_id || '') === filtreSousFam)
            .map(sf => {
              const articles = sf.articles.filter(a => !filtreMarque || a.marque_nom === filtreMarque);
              return {
                ...sf,
                articles,
                total_valeur: articles.reduce((s, a) => s + (getValeur(a) || 0), 0),
              };
            })
            .filter(sf => sf.articles.length > 0),
        }))
        .map(f => ({
          ...f,
          total_valeur: f.sous_familles.reduce((s, sf) => s + sf.total_valeur, 0),
        }))
        .filter(f => f.sous_familles.length > 0)
    : [];

  const totalGlobal = famillesFiltrees.reduce((s, f) => s + f.total_valeur, 0);

  return (
    <div className="space-y-5">
      <ReadOnlyBanner />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)' }}>
            Valorisation des stocks
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Valeur du stock à une date donnée · Famille → Sous-famille → Articles
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-end gap-3"
        style={{ borderColor: 'var(--border)' }}>

        {/* Famille */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Famille</label>
          <select
            value={filtreFamille}
            onChange={e => { setFiltreFamille(e.target.value); setFiltreSousFam(''); }}
            data-testid="valori-filtre-famille"
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', minWidth: 160 }}
          >
            <option value="">Toutes</option>
            {allFamilles.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
          </select>
        </div>

        {/* Sous-famille */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Sous-famille</label>
          <select
            value={filtreSousFam}
            onChange={e => setFiltreSousFam(e.target.value)}
            data-testid="valori-filtre-sousfam"
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', minWidth: 160 }}
          >
            <option value="">Toutes</option>
            {allSousFams.map(sf => <option key={sf.id} value={sf.id}>{sf.nom}</option>)}
          </select>
        </div>

        {/* Marque */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Marque</label>
          <select
            value={filtreMarque}
            onChange={e => setFiltreMarque(e.target.value)}
            data-testid="valori-filtre-marque"
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', minWidth: 140 }}
          >
            <option value="">Toutes</option>
            {allMarques.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="w-px self-stretch mx-1" style={{ background: 'var(--border)' }} />

        {/* Date */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            Date de valorisation
          </label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            data-testid="valori-date-input"
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>

        <div className="w-px self-stretch mx-1" style={{ background: 'var(--border)' }} />

        {/* Toggle Quantité */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Mode quantité</label>
          <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            {[{k:'physique', l:'Physique'}, {k:'comptable', l:'Comptable'}].map(({k, l}) => (
              <button key={k} onClick={() => setModeQty(k)} data-testid={`toggle-qty-${k}`}
                className="px-3 py-2 text-xs font-semibold transition-colors"
                style={{
                  background: modeQty === k ? 'var(--primary-solid)' : 'white',
                  color: modeQty === k ? 'white' : 'var(--text-muted)',
                }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Toggle Prix */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Mode prix</label>
          <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            {[{k:'pmp', l:'PMP / C.Revient'}, {k:'achat', l:'Prix achat HT'}].map(({k, l}) => (
              <button key={k} onClick={() => setModePrix(k)} data-testid={`toggle-prix-${k}`}
                className="px-3 py-2 text-xs font-semibold transition-colors"
                style={{
                  background: modePrix === k ? 'var(--primary-solid)' : 'white',
                  color: modePrix === k ? 'white' : 'var(--text-muted)',
                }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={calculate}
          disabled={loading}
          data-testid="valori-calculate-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: 'var(--primary-gradient)' }}
        >
          {loading ? (
            <span className="w-4 h-4 border-2 rounded-full animate-spin inline-block"
              style={{ borderColor: 'white', borderTopColor: 'transparent' }} />
          ) : (
            <Calculator size={16} />
          )}
          Calculer
        </button>

        <button
          onClick={printPdf}
          disabled={!data || pdfLoading}
          data-testid="valori-pdf-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
          style={{
            border: '1.5px solid var(--border)',
            color: data ? 'var(--primary-solid)' : 'var(--text-muted)',
            background: 'white',
          }}
          title={!data ? 'Calculez d\'abord la valorisation' : 'Imprimer en PDF (paysage A4)'}
        >
          {pdfLoading ? (
            <span className="w-4 h-4 border-2 rounded-full animate-spin inline-block"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
          ) : (
            <FileDown size={16} />
          )}
          PDF
        </button>
      </div>

      {/* Résultats */}
      {data && (
        <div className="space-y-3">
          {/* Bannière total */}
          <div className="bg-white rounded-xl border p-5 flex items-center justify-between"
            style={{ borderColor: 'var(--border)', borderLeft: '4px solid var(--primary-solid)' }}>
            <div>
              <p className="text-xs font-semibold tracking-wide mb-1"
                style={{ color: 'var(--text-muted)' }}>
                VALEUR TOTALE DU STOCK
              </p>
              <p className="text-3xl font-bold" style={{ color: 'var(--primary-solid)' }} data-testid="valori-total">
                {fmtEur(totalGlobal)}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Au {new Date(data.date_valorisation + 'T12:00:00').toLocaleDateString('fr-FR', {
                  day: '2-digit', month: 'long', year: 'numeric'
                })}
                {' · '}{agenceNom}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && nbSansPmp > 0 && (
                <button
                  onClick={openPmpPanel}
                  data-testid="btn-pmp-manquants"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors"
                  style={{
                    background: '#FEF3C7', borderColor: '#F59E0B',
                    color: '#B45309',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FDE68A'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FEF3C7'}
                >
                  <AlertTriangle size={13} />
                  {nbSansPmp} article{nbSansPmp > 1 ? 's' : ''} sans prix
                </button>
              )}
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'var(--primary-50, #EFF6FF)' }}>
                <TrendingUp size={26} style={{ color: 'var(--primary-solid)' }} />
              </div>
            </div>
          </div>

          {/* Table */}
          {data.familles.length === 0 ? (
            <div className="rounded-xl py-14 text-center" style={{ border: '1px dashed var(--border)' }}>
              <Package size={30} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Aucun article en stock à cette date
              </p>
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden bg-white"
              style={{ borderColor: 'var(--border)' }}>

              {/* En-tête colonnes */}
              <div className="grid text-xs font-semibold px-4 py-2.5 border-b"
                style={{
                  gridTemplateColumns: '10fr 3fr 3fr 3fr 3fr 3fr',
                  background: '#F5F5F3',
                  borderColor: 'var(--border)',
                  color: 'var(--text-muted)',
                }}>
                <span>Désignation</span>
                <span className="text-center">{/* Emplacement */}Emplacement</span>
                <span className="text-center">{labelQty}</span>
                <span className="text-right">{labelPrix}</span>
                <span className="text-center">Base prix</span>
                <span className="text-right">Valeur</span>
              </div>

              {famillesFiltrees.map(famille => {
                const famKey = famille.famille_id || '__sans_famille__';
                const famExpanded = expandedFamilles.has(famKey);

                return (
                  <div key={famKey}>
                    {/* Ligne famille */}
                    <button
                      type="button"
                      onClick={() => toggleFamille(famKey)}
                      data-testid={`famille-row-${famKey}`}
                      className="w-full grid text-left border-b hover:bg-gray-50 transition-colors"
                      style={{
                        gridTemplateColumns: '10fr 3fr 3fr 3fr 3fr 3fr',
                        borderColor: 'var(--border)',
                        background: '#FAFAF8',
                        padding: '10px 16px',
                      }}
                    >
                      <span className="flex items-center gap-2 font-bold text-sm"
                        style={{ color: 'var(--text)' }}>
                        {famExpanded
                          ? <ChevronDown size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                          : <ChevronRight size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />}
                        <Layers size={13} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                        {famille.famille_nom}
                        <span className="text-xs font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
                          ({famille.sous_familles.reduce((s, sf) => s + sf.articles.length, 0)} articles)
                        </span>
                      </span>
                      <span /><span /><span /><span />
                      <span className="text-right font-bold text-sm"
                        style={{ color: 'var(--primary-solid)' }}>
                        {fmtEur(famille.total_valeur)}
                      </span>
                    </button>

                    {/* Sous-familles */}
                    {famExpanded && famille.sous_familles.map(sf => {
                      const sfKey = `${famKey}::${sf.sous_famille_id || '__sans_sf__'}`;
                      const sfExpanded = expandedSFs.has(sfKey);

                      return (
                        <div key={sfKey}>
                          {/* Ligne sous-famille */}
                          <button
                            type="button"
                            onClick={() => toggleSF(sfKey)}
                            data-testid={`sf-row-${sfKey}`}
                            className="w-full grid text-left border-b transition-colors"
                            style={{
                              gridTemplateColumns: '10fr 3fr 3fr 3fr 3fr 3fr',
                              borderColor: 'var(--border-light, #EBEBEB)',
                              padding: '8px 16px',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F0F9FF'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                          >
                            <span className="flex items-center gap-2 text-sm font-semibold"
                              style={{ paddingLeft: 24, color: 'var(--text-secondary)' }}>
                              {sfExpanded
                                ? <ChevronDown size={13} style={{ flexShrink: 0 }} />
                                : <ChevronRight size={13} style={{ flexShrink: 0 }} />}
                              <Tag size={12} style={{ flexShrink: 0, color: '#6B7280' }} />
                              {sf.sous_famille_nom}
                              <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                                ({sf.articles.length})
                              </span>
                            </span>
                            <span /><span /><span /><span />
                            <span className="text-right font-semibold text-sm"
                              style={{ color: 'var(--text)' }}>
                              {fmtEur(sf.total_valeur)}
                            </span>
                          </button>

                          {/* Articles */}
                          {sfExpanded && sf.articles.map((art, idx) => {
                            const badge = BASE_PRIX_BADGE[art.base_prix] || BASE_PRIX_BADGE.non_renseigne;
                            const valeur = getValeur(art);
                            const qty    = getQty(art);
                            const prix   = getPrix(art);
                            return (
                              <div
                                key={art.article_id + String(idx)}
                                data-testid="valori-article-row"
                                className="grid border-b text-xs"
                                style={{
                                  gridTemplateColumns: '10fr 3fr 3fr 3fr 3fr 3fr',
                                  borderColor: 'var(--border-light, #EBEBEB)',
                                  padding: '7px 16px',
                                  background: 'white',
                                }}
                              >
                                {/* Désignation */}
                                <div className="flex items-center gap-1.5 min-w-0"
                                  style={{ paddingLeft: 48 }}>
                                  <span className="font-mono px-1.5 py-0.5 rounded flex-shrink-0 text-xs"
                                    style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                                    {art.article_ref || '—'}
                                  </span>
                                  <span className="truncate" style={{ color: 'var(--text)' }}>
                                    {[art.article_nom, art.marque_nom, art.modele_nom].filter(Boolean).join(' · ')}
                                  </span>
                                  {art.numero_serie && (
                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded font-mono text-xs"
                                      style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
                                      S/N {art.numero_serie}
                                    </span>
                                  )}
                                </div>

                                {/* Emplacement */}
                                <span className="text-center truncate"
                                  style={{ color: 'var(--text-secondary)', minWidth: 0, overflow: 'hidden' }}
                                  title={art.zone_nom || ''}>
                                  {art.zone_nom || '—'}
                                </span>

                                {/* Quantité */}
                                <span className="text-center font-mono tabular-nums"
                                  style={{ color: 'var(--text-secondary)', minWidth: 0, overflow: 'hidden' }}>
                                  {fmtQty(qty)}
                                </span>

                                {/* Prix */}
                                <span className="text-right font-mono tabular-nums"
                                  style={{ color: prix > 0 ? 'var(--text-secondary)' : 'var(--text-disabled)', minWidth: 0, overflow: 'hidden' }}>
                                  {prix > 0 ? fmtEur(prix) : '—'}
                                </span>

                                {/* Badge base prix */}
                                <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0, overflow: 'hidden' }}>
                                  <span className="px-1.5 py-0.5 rounded text-xs font-medium truncate"
                                    style={{ background: badge.bg, color: badge.color, maxWidth: '100%' }}>
                                    {badge.label}
                                  </span>
                                </div>

                                {/* Valeur */}
                                <span className="text-right font-semibold font-mono tabular-nums"
                                  style={{ color: valeur > 0 ? 'var(--text)' : 'var(--text-disabled)', minWidth: 0, overflow: 'hidden' }}>
                                  {fmtEur(valeur)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Panel PMP manquants (admin uniquement) ─────────────────────── */}
      {isAdmin && (
        <SidePanel
          open={showPmpPanel}
          onOpenChange={setShowPmpPanel}
          title="Initialiser les PMP manquants"
          width={900}
        >
          <div className="flex flex-col h-full">
            {/* Barre d'actions globales */}
            <div className="flex flex-wrap items-end gap-3 p-4 border-b"
              style={{ borderColor: 'var(--border)', background: '#FAFAF8' }}>
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  Date globale (appliquer à la sélection)
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="date" value={bulkDate}
                    onChange={e => setBulkDate(e.target.value)}
                    data-testid="pmp-bulk-date"
                    className="px-3 py-1.5 rounded-lg border text-sm"
                    style={{ borderColor: 'var(--border)' }}
                  />
                  <button
                    onClick={applyBulkDate}
                    data-testid="pmp-apply-bulk-date"
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                  >
                    Appliquer à la sélection
                  </button>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {pmpItems.filter(i => i.selected).length} / {pmpItems.length} sélectionnés
                </span>
                <button
                  onClick={savePmp}
                  disabled={savingPmp}
                  data-testid="pmp-save-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: 'var(--primary-gradient)' }}
                >
                  {savingPmp
                    ? <span className="w-4 h-4 border-2 rounded-full animate-spin inline-block"
                        style={{ borderColor: 'white', borderTopColor: 'transparent' }} />
                    : <Save size={14} />}
                  Enregistrer
                </button>
              </div>
            </div>

            {/* Table */}
            {pmpItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Package size={28} style={{ color: 'var(--text-disabled)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Tous les articles ont un PMP
                </p>
              </div>
            ) : (
              <div className="overflow-auto flex-1">
                {/* En-tête */}
                <div className="grid text-xs font-semibold px-4 py-2 sticky top-0 border-b"
                  style={{
                    gridTemplateColumns: '32px 90px 1fr 90px 90px 100px 120px 130px 44px',
                    background: '#F5F5F3', borderColor: 'var(--border)',
                    color: 'var(--text-muted)',
                  }}>
                  <button onClick={toggleAll} className="flex items-center" data-testid="pmp-toggle-all">
                    {pmpItems.every(i => i.selected)
                      ? <CheckSquare size={14} style={{ color: 'var(--primary-solid)' }} />
                      : <Square size={14} style={{ color: 'var(--text-muted)' }} />}
                  </button>
                  <span>Réf.</span>
                  <span>Désignation</span>
                  <span className="text-right">Qté</span>
                  <span>Stock</span>
                  <span className="text-right">Prix achat</span>
                  <span className="text-right">PMP (€)</span>
                  <span>Date entrée</span>
                  <span></span>
                </div>

                {pmpItems.map((item, idx) => (
                  <div
                    key={item.entry_id || item.article_id + idx}
                    data-testid="pmp-item-row"
                    className="grid items-center px-4 py-1.5 border-b text-xs"
                    style={{
                      gridTemplateColumns: '32px 90px 1fr 90px 90px 100px 120px 130px 44px',
                      borderColor: 'var(--border-light, #EBEBEB)',
                      background: item.selected ? '#FAFFFE' : 'white',
                    }}
                  >
                    <button
                      onClick={() => updateItem(idx, 'selected', !item.selected)}
                      className="flex items-center"
                    >
                      {item.selected
                        ? <CheckSquare size={14} style={{ color: 'var(--primary-solid)' }} />
                        : <Square size={14} style={{ color: 'var(--text-muted)' }} />}
                    </button>

                    <span className="font-mono text-xs px-1.5 py-0.5 rounded truncate"
                      style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                      {item.article_ref || '—'}
                    </span>

                    <div className="min-w-0 px-2">
                      <p className="truncate" style={{ color: 'var(--text)' }}>{item.article_nom}</p>
                      {item.famille_nom && (
                        <p className="truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                          {item.famille_nom}
                        </p>
                      )}
                    </div>

                    <span className="text-right font-mono tabular-nums"
                      style={{ color: 'var(--text-secondary)' }}>
                      {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(item.quantite)}
                    </span>

                    <span className="truncate text-xs px-1" style={{ color: 'var(--text-muted)' }}>
                      {item.stock_nom}
                    </span>

                    <span className="text-right font-mono tabular-nums text-xs"
                      style={{ color: item.prix_achat ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>
                      {item.prix_achat != null
                        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(item.prix_achat)
                        : '—'}
                    </span>

                    <div className="flex justify-end">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.pmp}
                        onChange={e => updateItem(idx, 'pmp', e.target.value)}
                        data-testid="pmp-item-input"
                        placeholder="0.00"
                        className="w-24 text-right px-2 py-1 rounded border text-xs font-mono"
                        style={{
                          borderColor: item.pmp && parseFloat(item.pmp) > 0
                            ? 'var(--primary-solid)' : 'var(--border)',
                        }}
                      />
                    </div>

                    <div className="pl-1">
                      <input
                        type="date"
                        value={item.date_entree}
                        onChange={e => updateItem(idx, 'date_entree', e.target.value)}
                        data-testid="pmp-item-date"
                        className="w-full px-2 py-1 rounded border text-xs"
                        style={{ borderColor: 'var(--border)' }}
                      />
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={() => saveOne(idx)}
                        disabled={savingIdx === idx || !item.pmp || parseFloat(item.pmp) <= 0}
                        data-testid="pmp-item-save-btn"
                        title="Valider ce PMP"
                        className="w-7 h-7 flex items-center justify-center rounded transition-colors disabled:opacity-40"
                        style={{ background: 'var(--primary-50, #EFF6FF)', color: 'var(--primary-solid)' }}
                        onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--primary-solid)'; e.currentTarget.style.color = 'white'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--primary-50, #EFF6FF)'; e.currentTarget.style.color = 'var(--primary-solid)'; }}
                      >
                        {savingIdx === idx
                          ? <span className="w-3 h-3 border-2 rounded-full animate-spin inline-block"
                              style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                          : <Check size={13} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SidePanel>
      )}
    </div>
  );
}
